import React from 'react';
import { sortBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { useSelector } from 'react-redux';
import { Form, Button, Select, Card, Row, Col } from '../../../../ui-kit';
import { IconPlus, IconTrash, IconBars, IconCheck, IconXmark } from '../../../../ui-kit/icons';
import SettingsForm from '../../ui-kit/SettingsForm';
import { SortableContainer, SortableElement, SortableHandle, arrayMove } from 'react-sortable-hoc';
import { selectors as groupSelectors } from '../../../../../state/modules/groups';
import { selectors as skillSelectors } from '../../../../../state/modules/skills';
import { selectors as standbyDutyFunctionSelectors } from '../../../../../state/modules/standby-duty-functions';
import { selectors as availabilityCodeSelectors } from '../../../../../state/modules/availability-codes';
// Other utilities
import SkillIcon from '../../../../general/SkillIcon';

const DragHandle = SortableHandle(() => <IconBars />);

const FilterRuleCard = ({
  filter,
  id,
  filterRule,
  handleFilterChanged,
  generateSelectFor,
  groups,
  skills,
  standbyDutyFunctions,
  availabilityCodes
}) => {
  const handleClick = (e, paramName, filter, id) => {
    const value = e.target.checked;
    filter = { ...filter, [paramName]: value };
    handleFilterChanged(id, filter);
  };
  if (!filter) return null;
  return (
    <Card
      style={{
        background: filterRule === 'include' ? 'var(--bs-info-border-subtle)' : 'var(--bs-secondary-border-subtle)'
      }}
    >
      <Card.Header>
        <Form>
          <Form.Check type="checkbox">
            <Form.Check.Input
              type="checkbox"
              id={`${filterRule}Checkbox${id}`}
              defaultChecked={filter[`${filterRule}_enabled`] ? true : false}
              onClick={e => handleClick(e, `${filterRule}_enabled`, filter, id)}
            />
            <Form.Check.Label htmlFor={`${filterRule}Checkbox${id}`}>{I18n.t(filterRule)}</Form.Check.Label>
          </Form.Check>
        </Form>
      </Card.Header>
      <Card.Body className={filter[`${filterRule}_enabled`] ? '' : 'collapse'}>
        <Row>
          <Col>{I18n.t('activerecord.models.group')}</Col>
          <Col>{I18n.t('activerecord.models.skill')}</Col>
        </Row>
        <Row>
          <Col>
            <Row>
              <Col>{generateSelectFor('group', groups, `${filterRule}_group_ids`)}</Col>
              <Col>{generateSelectFor('skill', skills, `${filterRule}_skill_ids`)}</Col>
            </Row>
            <Row>
              <Col>{I18n.t('activerecord.models.standby_duty_function')}</Col>
              <Col>{I18n.t('activerecord.models.schedule/availability_code.one')}</Col>
            </Row>
            <Row>
              <Col>
                {generateSelectFor('standby_duty_function', standbyDutyFunctions, `${filterRule}_duty_function_ids`)}
              </Col>
              <Col>
                {generateSelectFor(
                  'schedule/availability_code',
                  availabilityCodes,
                  `${filterRule}_availability_code_ids`
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const FilterCard = ({
  groups,
  skills,
  standbyDutyFunctions,
  availabilityCodes,
  filter,
  handleFilterChanged,
  id,
  deleteFilter,
  showMinimumCrew
}) => {
  // Generate the collection of groups for the group select
  const groupOptions = sortBy(groups, ['name']).map(group => {
    const textLabel = group.name;
    const label = <>{group.name}</>;
    return { value: group.id, textLabel, label };
  });

  // Generates the collection of elements to be displayed in the select input
  const selectOptions = collection => {
    if (collection === groups) return groupOptions;
    const isAvailabilityCode = collection === availabilityCodes;
    return sortBy(collection, ['name']).map(element => {
      const textLabel = element.name || element.short_code;
      const label = (
        <>
          <SkillIcon skill={element} /> {element.name}{' '}
          {/* if the selector is for availability codes, add a visual indicator for crewing availability */}
          {isAvailabilityCode && element.available_for_crewing && <IconCheck className="text-success" />}
          {isAvailabilityCode && !element.available_for_crewing && <IconXmark className="text-danger" />}
        </>
      );
      const data = { value: element.id, textLabel, label };
      // availability codes should also share the available for crewing status to display an indicator
      if (isAvailabilityCode) data['available'] = element.available_for_crewing;
      return data;
    });
  };

  // Chooses which elements to display as selected in the select input
  const selectedOptions = (collection, paramName) => {
    if (!filter[paramName] || filter[paramName].length === 0) return null;
    return selectOptions(collection).filter(element => filter[paramName].includes(element.value));
  };

  const filterOption = (op, filterValue) => op.data.textLabel.toLowerCase().includes(filterValue.toLowerCase());

  // Event handlers for different inputs
  const handleTitleChange = event => {
    filter = { ...filter, title: event.target.value };
    handleFilterChanged(id, filter);
  };

  const handleMinimumCrewChange = event => {
    const value = Number.parseInt(event.target.value);
    filter = {
      ...filter,
      minimumCrew: Number.isNaN(value) ? undefined : value
    };
    handleFilterChanged(id, filter);
  };

  const handleSelectChange = (events, paramName) => {
    // Availability codes control whether the filter should only look for
    // available members or all members. If an availability code that is
    // NOT available for crewing is selected, we must indicate that this filter
    // should look for all members, not only available ones.
    if (paramName === 'include_availability_code_ids') {
      // if any availability code selected is not available for crewing,
      // onlyAvailable should become false.
      filter = { ...filter, onlyAvailable: !events.some(e => !e.available) };
    }
    const values = events.map(e => e.value);
    filter = { ...filter, [paramName]: values };
    handleFilterChanged(id, filter);
  };

  const generateSelectFor = (modelName, collection, paramName) => {
    let translation = `activerecord.models.${modelName}`;
    if (typeof I18n.t(translation) !== 'string') translation += '.one';
    return (
      <Select
        value={selectedOptions(collection, paramName)}
        isMulti
        onChange={e => handleSelectChange(e, paramName)}
        options={selectOptions(collection)}
        isSearchable={true}
        isClearable={true}
        filterOption={filterOption}
        menuPortalTarget={document.body}
        menuPosition={'fixed'}
      />
    );
  };

  return (
    <Card className="mb-2" style={{ zIndex: 99_999 }}>
      <Card.Body>
        <Row>
          <Col sm={1} style={{ width: '3%' }} />
          <Col sm={3}>{I18n.t('attributes.name')}</Col>
          {showMinimumCrew && <Col sm={3}>{I18n.t('dashboards.widget_archetypes.ridership_details.minimum_crew')}</Col>}
          <Col />
        </Row>
        <Row>
          <Col sm={1} className="align-center" style={{ width: '3%' }}>
            <DragHandle />
          </Col>
          <Col sm={3}>
            <Form.Control
              type="text"
              name="title"
              placeholder={I18n.t('attributes.description')}
              value={filter.title}
              onChange={handleTitleChange}
            />
          </Col>
          {showMinimumCrew && (
            <Col sm={3}>
              <Form.Control
                type="text"
                name="minimumCrew"
                placeholder="0"
                value={filter.minimumCrew}
                onChange={handleMinimumCrewChange}
              />
            </Col>
          )}
          <Col sm={4} />
          <Col className="align-center">
            <Button variant="danger" size="xs" onClick={() => deleteFilter(id)} className="float-right mb-1">
              <IconTrash />
            </Button>
          </Col>
        </Row>
        <br />
        <Row>
          <Col sm={1} style={{ width: '3%' }} />
          <Col>
            <FilterRuleCard
              filter={filter}
              id={id}
              filterRule={'include'}
              handleFilterChanged={handleFilterChanged}
              generateSelectFor={generateSelectFor}
              groups={groups}
              skills={skills}
              standbyDutyFunctions={standbyDutyFunctions}
              availabilityCodes={availabilityCodes}
            />
            <br />
            <FilterRuleCard
              filter={filter}
              id={id}
              filterRule={'exclude'}
              handleFilterChanged={handleFilterChanged}
              generateSelectFor={generateSelectFor}
              groups={groups}
              skills={skills}
              standbyDutyFunctions={standbyDutyFunctions}
              availabilityCodes={availabilityCodes}
            />
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

const SortableFilterCard = SortableElement(props => <FilterCard {...props} />);

const SortableFilterList = SortableContainer(props => (
  <div className="mt-3">
    {props.filters.map((filter, index) => (
      <SortableFilterCard
        key={index}
        id={index}
        index={index}
        filter={filter}
        deleteFilter={props.deleteFilter}
        handleFilterChanged={props.handleFilterChanged}
        standbyDutyFunctions={props.standbyDutyFunctions}
        skills={props.skills}
        groups={props.groups}
        availabilityCodes={props.availabilityCodes}
        showMinimumCrew={props.showMinimumCrew}
      />
    ))}
  </div>
));

const Settings = ({ updateWidgetSettingsField, widgetArchetypeId, widgetSettings }) => {
  const groups = useSelector(groupSelectors.values);
  const skills = useSelector(skillSelectors.values);
  const standbyDutyFunctions = useSelector(standbyDutyFunctionSelectors.values);
  const availabilityCodes = useSelector(availabilityCodeSelectors.values);

  let { dayCheckpoint, nightCheckpoint, filters } = widgetSettings;
  // the following variable indicates when should the extra setting options be displayed
  const shiftTimesEnabled = widgetArchetypeId === 'ridership_details';

  const showMinimumCrew = widgetArchetypeId === 'ridership_details';

  filters = filters || [];
  filters = filters.map(f => (f.include_enabled === undefined ? { ...f, include_enabled: true } : f));
  filters = filters.map(f => (f.exclude_enabled === undefined ? { ...f, exclude_enabled: true } : f));

  dayCheckpoint = Number.isInteger(dayCheckpoint) ? { hour: Number(dayCheckpoint || 10), minute: 0 } : dayCheckpoint;
  nightCheckpoint = Number.isInteger(nightCheckpoint)
    ? { hour: Number(nightCheckpoint || 19), minute: 0 }
    : nightCheckpoint;

  // Auxiliary functions
  const addNewFilter = () => {
    const newFilter = {
      title: '',
      id: uuidv4(),
      include_group_ids: [],
      include_skill_ids: [],
      include_duty_function_ids: [],
      include_availability_code_ids: [],
      exclude_group_ids: [],
      exclude_skill_ids: [],
      exclude_duty_function_ids: [],
      exclude_availability_code_ids: [],
      onlyAvailable: true,
      include_enabled: true,
      exclude_enabled: false
    };

    updateWidgetSettingsField('filters', [...filters, newFilter]);
  };

  const deleteFilter = index => {
    const toRemove = filters[index];
    updateWidgetSettingsField(
      'filters',
      filters.filter(f => f !== toRemove)
    );
  };

  const handleFilterChanged = (index, filter) =>
    updateWidgetSettingsField(
      'filters',
      filters.map((f, i) => (i === index ? filter : f))
    );

  const changeDayCheckpoint = newValue => updateWidgetSettingsField('dayCheckpoint', newValue);
  const changeNightCheckpoint = newValue => updateWidgetSettingsField('nightCheckpoint', newValue);

  const onSortEnd = ({ oldIndex, newIndex }) =>
    updateWidgetSettingsField('filters', arrayMove(filters, oldIndex, newIndex));

  return (
    <>
      <SettingsForm
        withAutoScroll={true}
        withAutoResizeHeight={true}
        updateWidgetSettingsField={updateWidgetSettingsField}
        widgetSettings={widgetSettings}
      >
        {shiftTimesEnabled && (
          <SettingsForm.Section>
            <SettingsForm.Section.Header>
              {I18n.t('dashboards.widget_archetypes.shared.settings.checkpoints')}
            </SettingsForm.Section.Header>
            <SettingsForm.Section.Content className="d-flex flex-inline">
              <Col sm={3}>
                <SettingsForm.CheckpointSelector
                  name={'dayCheckpoint'}
                  value={dayCheckpoint}
                  checkpoint={0}
                  changeTime={changeDayCheckpoint}
                  label={I18n.t('dashboards.widget_archetypes.shared.settings.day_checkpoint')}
                />
              </Col>
              <Col sm={3} className="vr ms-3 me-3" />
              <Col sm={3}>
                <SettingsForm.CheckpointSelector
                  name={'nightCheckpoint'}
                  value={nightCheckpoint}
                  checkpoint={dayCheckpoint.hour + 1}
                  changeTime={changeNightCheckpoint}
                  label={I18n.t('dashboards.widget_archetypes.shared.settings.night_checkpoint')}
                />
              </Col>
            </SettingsForm.Section.Content>
          </SettingsForm.Section>
        )}
      </SettingsForm>
      <SortableFilterList
        filters={filters}
        onSortEnd={onSortEnd}
        useDragHandle={true}
        deleteFilter={deleteFilter}
        handleFilterChanged={handleFilterChanged}
        standbyDutyFunctions={standbyDutyFunctions}
        skills={skills}
        groups={groups.filter(g => g.type !== 'team')}
        availabilityCodes={availabilityCodes}
        showMinimumCrew={showMinimumCrew}
      />
      <Button variant="success" onClick={addNewFilter}>
        <IconPlus /> {I18n.t('dashboards.widget_archetypes.shared.settings.new_filter')}
      </Button>
    </>
  );
};

export default Settings;
